import React from 'react'
import CountUp from 'react-countup'
import numeral from 'numeral'
import moment from 'moment'
import { Tooltip } from 'react-tooltip'

import * as Constants from '../../../helpers/constants'
import * as Helper from '../../../helpers/helper'

// webapp: defaultMemberFilterData (analytics/components/*/index.js)
export const ALL_MEMBERS_FILTER_OPTION = {
  value: { id: 1, role: 'all', email: '' },
  label: 'All Members (Organization)'
}

export const DEFAULT_DATE_RANGE_FILTER = {
  filter_label: 'Date',
  label: 'Last 7 days (Including Today)',
  value: 'ps_last_7_days_including_today'
}

export const ANALYTICS_TAB_LABELS = {
  overview: 'Overview',
  email_verifier: 'Email Verifier',
  email_finder: 'Email Finder',
  prospect: 'Prospect',
  form_guard: 'Form Guard',
  data_pulse: 'Data Pulse',
  team: 'Team',
}

// webapp sidebar order: overview, ev, ef, prospect, form_guard, data_pulse, team
export const ANALYTICS_TAB_ORDER = ['overview', 'email_verifier', 'email_finder', 'prospect', 'form_guard', 'data_pulse', 'team']

// webapp filters.js:118 — member dropdown only for org owner/admin
export const isOrgAdminView = (viewedUser) => {
  return viewedUser?.user_type === 'organization' && ['owner', 'admin'].includes(viewedUser?.user_role)
}

const getRoleConfig = (userRole) => {
  return Constants.DISPLAY_USER_ROLES[userRole] || Constants.DISPLAY_USER_ROLES.owner
}

// webapp gating = ANALYTICS_MENUS (per deployment) ∩ role analytic_menus (route.js),
// plus sidebar.js's extra org owner/admin check on the team link
export const getVisibleTabs = (client, viewedUser) => {
  const clientMenus = Constants.CLIENT_ANALYTICS_MENUS[client] || []
  const roleMenus = getRoleConfig(viewedUser?.user_role).analytic_menus
  return ANALYTICS_TAB_ORDER.filter((menu) => {
    if (!clientMenus.includes(menu) || !roleMenus.includes(menu)) return false
    if (menu === 'team') return isOrgAdminView(viewedUser)
    return true
  })
}

// webapp filters.js individualUserDefaultFilter / options[user.email]
export const buildSelfMemberOption = (viewedUser) => {
  return {
    value: { id: viewedUser.id, role: viewedUser.user_role, email: viewedUser.email },
    label: (viewedUser.email || '') + ' (' + getRoleConfig(viewedUser.user_role).display_name + ')'
  }
}

// webapp getAllUsersList response mapping (analytics/components/*/index.js)
export const buildMemberOptions = (result) => {
  const memberFilters = [ALL_MEMBERS_FILTER_OPTION]
  result.forEach((item) => {
    const nameList = _isEmptyString(item.name) ? item.email : item.name + ' / ' + item.email
    memberFilters.push({
      value: { id: item._id, role: item.user_role, email: item.email },
      label: nameList + ' (' + getRoleConfig(item.user_role).display_name + ')'
    })
  })
  return memberFilters
}

const _isEmptyString = (value) => {
  return value === undefined || value === null || String(value).trim() === ''
}

// webapp getDateRange: preset string pass-through; custom range as calendar
// dates the backend re-anchors to the customer's timezone day boundaries
// (cocloud2 getStartAndEndDatesFromDateRange)
export const getDateRangeForApi = (dateRangeFilter) => {
  const value = dateRangeFilter?.value
  if (typeof value === 'string') return value
  if (value?.startDate && value?.endDate) {
    return {
      start_date: moment(value.startDate).format('YYYY-MM-DD'),
      end_date: moment(value.endDate).format('YYYY-MM-DD')
    }
  }
  return null
}

/**
 * Verbatim port of webapp getApiParams (analytics/components/<tab>/index.js)
 * with `user` = the viewed customer, so the CRM admin gets exactly the params
 * the customer's own session would produce. The webapp's "self" cases omit ids
 * and let the backend infer the authenticated user — the CRM sends user_id
 * explicitly. Returns { filters, selectedUserState } where filters is the JSON
 * for the report API's `filters` query param.
 */
export const buildApiFilters = ({ viewedUser, memberFilter, dateRangeFilter, accountFilter = null }) => {
  const { scope, selectedUserState } = getOrgMemberFilters({ viewedUser, memberFilter })

  const filters = {
    date_range: getDateRangeForApi(dateRangeFilter),
    ...scope
  }

  // Data Pulse only — the other analytics tabs never pass an account filter.
  if (accountFilter) {
    filters.account_id = accountFilter.value
  }

  return { filters, selectedUserState }
}

/**
 * The org/member filter params the report runs under (org_id / member_id / user_id), without the
 * date range. Split out of buildApiFilters so the Data Pulse account picker can be fetched with the
 * SAME scope — otherwise it would offer accounts the report cannot slice by.
 */
export const getOrgMemberFilters = ({ viewedUser, memberFilter }) => {
  const scope = {}

  const isOrgRole = (user, roles) => {
    return user.user_type === 'organization' && roles.includes(user.user_role)
  }

  const selectedUserState = isOrgAdminView(viewedUser)
    ? memberFilter
    : buildSelfMemberOption(viewedUser)

  if (isOrgRole(viewedUser, ['owner', 'admin'])) {
    scope.org_id = viewedUser.org_id
    scope.member_id = selectedUserState?.value.id
  }

  if (isOrgRole(viewedUser, ['owner'])) {
    const invalidRoles = ['all', 'admin', 'analyst']

    if (scope.org_id && !(invalidRoles.includes(selectedUserState?.value.role))) {
      delete scope.org_id
    }

    if (scope.member_id && !['admin', 'analyst'].includes(selectedUserState?.value.role)) {
      delete scope.member_id
    }
  } else if (isOrgRole(viewedUser, ['admin'])) {
    const invalidRoles = ['all', 'owner', 'analyst']

    if (scope.org_id && !invalidRoles.includes(selectedUserState?.value.role)) {
      delete scope.org_id
    }

    if (scope.member_id && !['owner', 'analyst'].includes(selectedUserState?.value.role)) {
      delete scope.member_id
    }
  }

  // webapp self cases resolve from the auth session; CRM must be explicit
  scope.user_id = scope.org_id ? viewedUser.id : (selectedUserState?.value?.id || viewedUser.id)

  return { scope, selectedUserState }
}

// Data Pulse account-picker labels. Matches the customer app's getIntegrationDisplayName
// (cowebapp data_pulse/helpers/helper.js) — this analytics view is a replica of that page, so the
// labels must read the same. Deliberately not INTEGRATION_PLATFORMS_NAME, which says 'Hubspot'.
export const DATA_PULSE_PROVIDER_DISPLAY_NAMES = {
  hubspot: 'HubSpot',
  salesforce: 'Salesforce'
}

// data_pulse_user_settings status for a disconnected account. It keeps up to 700 days of usage
// history, so it stays in the picker — just labelled.
export const DATA_PULSE_ABANDONED_ACCOUNT_STATUS = 'abandoned'

// webapp getTableHeading (analytics/components/*/index.js)
export const getTableHeading = (selectedRole, breakdown) => {
  if (selectedRole === 'all') {
    return 'Member-wise Breakup'
  } else if (selectedRole !== 'all' && breakdown === 'HOUR') {
    return 'Hourly Breakup'
  } else if (selectedRole !== 'all' && breakdown === 'MONTH') {
    return 'Monthly Breakup'
  } else {
    return 'Day-wise Breakup'
  }
}

// webapp table dateFormatter (report_*_table.js)
export const formatReportDate = (cell, breakdown) => {
  if (breakdown === 'DAY') {
    return Helper.formatTime(cell, 'ddd, Do MMM YYYY')
  } else if (breakdown === 'HOUR') {
    return Helper.formatTime(cell, 'ddd, Do MMM YY, h:mm a')
  } else {
    return Helper.formatTime(cell, 'MMMM YYYY')
  }
}

// webapp cell formatter used across report tables
export const localeNumberFormatter = (zeroAs = '0') => (cell) => {
  if (cell === 0) {
    return zeroAs
  }
  return Number(cell).toLocaleString()
}

// port of webapp Helper.getMetrics (cowebapp helper.js:203) — react-countup
// (maintained) replaces react-count-to; react-tooltip v5 replaces v3
export const getMetrics = (value, precision, prefix, suffix, heading, isMetricsLoading) => {
  if (isMetricsLoading) {
    return (
      <span title="Loading...">
        -
      </span>
    )
  }
  let counter =
    value > 999
      ? numeral(value)
        .format('0.00a')
        .slice(0, -1)
      : value
  let abbr =
    value > 999
      ? numeral(value)
        .format('0.00a')
        .slice(-1)
      : ''
  let decimalPrecision = precision
  if (value < 999 && heading !== 'Found Percentage' && heading !== 'Bounce Detected') {
    decimalPrecision = 0
  }

  let tooltipText = Number(value).toLocaleString('en-US')

  if (heading === 'Email Found' || heading === 'Total Verified') {
    tooltipText = Number(value)
  }
  if (suffix)
    tooltipText = tooltipText + ' ' + suffix
  if (prefix)
    tooltipText = prefix + ' ' + tooltipText

  return (
    <>
      <span
        data-tooltip-id={`${heading}`}
        data-tooltip-content={`${tooltipText}`}
      >
        {prefix}
        <CountUp
          end={Number(counter)}
          duration={1.5}
          decimals={decimalPrecision}
          separator=""
        />
        {Helper.capitalizeFirstLetter(abbr)}
        {suffix}
      </span>
      <Tooltip
        className="custom-header-tooltip"
        id={`${heading}`}
        place="bottom"
      />
    </>
  )
}

// webapp org tables call truncate-middle with back=0 — i.e. plain end-truncation
export const truncateEnd = (str, maxChars) => {
  const value = String(str ?? '')
  return value.length <= maxChars ? value : value.slice(0, maxChars) + '...'
}

// webapp table count-up cell (bounceFormatter/estCostFormatter in report tables)
export const getCountUpCell = (value, { precision = 0, prefix = '', suffix = '', title = '' } = {}) => {
  let counter = value > 999 ? numeral(value).format('0.00a').slice(0, -1) : value
  let abbr = value > 999 ? numeral(value).format('0.00a').slice(-1) : ''
  return (
    <p
      className="stat-value"
      title={title}
      style={{ marginBottom: '0px' }}
    >
      {prefix}
      <CountUp
        end={Number(counter)}
        duration={1.5}
        decimals={precision}
        separator=""
      />
      {Helper.capitalizeFirstLetter(abbr)}
      {suffix}
    </p>
  )
}
